import { LANGUAGE } from 'constants/general';
import { LanguageCode } from 'enums';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { updateLanguage } from 'store/reducer/setting/slice';
import { languageDropDownSelector } from 'store/reducer/setting/selectors';
import { useCallback, useMemo } from 'react';
import { changeLanguage } from 'services/i18n.service';
import DropdownMenu from 'components/ui/DropdownMenu';
import { MenuItem } from 'interfaces';
import { BaseButtonXs } from 'components/ui/BaseButton';

const LOCALE_COUNTRY: Record<string, string> = {
  [LanguageCode.EN]: 'GB',
  [LanguageCode.ES]: 'ES'
};

const languageMenuItems: MenuItem[] = [
  {
    id: LanguageCode.EN,
    text: 'EN',
    attribAction: 'select-language-en'
  },
  {
    id: LanguageCode.ES,
    text: 'ES',
    attribAction: 'select-language-es'
  }
];

const getFlagUrl = (langCode: string) => {
  const cc = (LOCALE_COUNTRY[langCode] || 'US').toLowerCase();
  return `https://flagcdn.com/${cc}.svg`;
};

const isSpanishLanguage = (language?: string) =>
  language === LANGUAGE.ES ||
  language === LanguageCode.ES ||
  language?.toLowerCase() === LanguageCode.ES;

const resolveLanguageCode = (language?: string): LanguageCode =>
  isSpanishLanguage(language) ? LanguageCode.ES : LanguageCode.EN;

export default function LanguageDropDown() {
  const dispatch = useAppDispatch();
  const { language } = useAppSelectorShallow(languageDropDownSelector);

  const activeLanguageCode = useMemo(
    () => resolveLanguageCode(language),
    [language]
  );

  const handleLanguageItemClick = useCallback(
    (menuItem: MenuItem) => {
      const code =
        menuItem.id === LanguageCode.ES ? LanguageCode.ES : LanguageCode.EN;
      changeLanguage(code);
      dispatch(updateLanguage(code));
    },
    [dispatch]
  );

  const renderLanguageMenuItem = useCallback(
    (menuItem: MenuItem) => {
      const isSelected = menuItem.id === activeLanguageCode;
      return (
        <BaseButtonXs
          onClick={() => {
            handleLanguageItemClick(menuItem);
          }}
          data-testid={`language-option-${menuItem.id}`}
          variant={isSelected ? 'primary' : 'secondary'}
          additionalStyles='w-fit whitespace-nowrap ~px-1.5/3 ~py-0.5/1 my-0.5 flex items-center ~gap-0.5/1'
          icon={
            <img
              src={getFlagUrl(menuItem.id || '')}
              alt={`${menuItem.text} flag`}
              className='~w-3/3.5 ~h-2/2.5 object-cover rounded-sm'
            />
          }
          title={menuItem.text || ''}
        />
      );
    },
    [handleLanguageItemClick, activeLanguageCode]
  );

  const currentLanguageInfo = useMemo(() => {
    const langCode = activeLanguageCode;
    const langText = langCode === LanguageCode.ES ? 'ES' : 'EN';
    return { langCode, langText };
  }, [activeLanguageCode]);

  return (
    <DropdownMenu
      subMenus={languageMenuItems}
      contentClassName='shadow rounded-md !w-fit bg-primary-50 dark:bg-secondary-dark-200 border border-primary-500 dark:border-secondary-dark-500 !~p-0.5/2'
      renderMenuItem={renderLanguageMenuItem}
      renderSubMenuStyle={() => 'w-fit whitespace-nowrap'}>
      <BaseButtonXs
        data-testid='language-dropdown'
        aria-label='Select language'
        aria-haspopup='true'
        variant='secondary'
        additionalStyles='w-fit whitespace-nowrap ~px-2/3.5 ~py-1/[0.35rem] flex items-center gap-1.5'
        icon={
          <img
            src={getFlagUrl(currentLanguageInfo.langCode)}
            alt={`${currentLanguageInfo.langText} flag`}
            className='~w-3/4 ~h-2/3 object-cover rounded-sm'
          />
        }
        title={currentLanguageInfo.langText || ''}
      />
    </DropdownMenu>
  );
}
